import { useCallback, useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../hooks/useToast';
import {
  ApiError,
  getAdminTtarLink,
  removeAdminTtarLink,
  setAdminTtarLink,
} from '../../lib/api';
import { formatLocal, formatVenueTime } from '../../lib/time';
import { PS, T } from '../../lib/theme';
import type { TtarLinkState } from '../../lib/types';

interface Props {
  userId: string;
}

type PendingConfirmation = 'replace' | 'remove' | null;

export function TtarLinkPanel({ userId }: Props) {
  const { push } = useToast();
  const loader = useCallback((signal: AbortSignal) => getAdminTtarLink(userId, signal), [userId]);
  const query = useApi<TtarLinkState>(loader);
  const [confirmedLink, setConfirmedLink] = useState<TtarLinkState | null>(null);
  const [draftId, setDraftId] = useState('');
  const [pending, setPending] = useState<PendingConfirmation>(null);
  const [busy, setBusy] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const currentUserIdRef = useRef(userId);
  const mutationIdRef = useRef(0);

  currentUserIdRef.current = userId;

  // A route parameter can change without React remounting this panel. Do not
  // carry a server-confirmed identity from the previous user's detail page.
  useEffect(() => {
    mutationIdRef.current += 1;
    setConfirmedLink(null);
    setDraftId('');
    setPending(null);
    setBusy(false);
    setMutationError(null);
  }, [userId]);

  const link = confirmedLink ?? query.data;
  const candidateId = draftId.trim();

  const applyLink = async (confirm: boolean) => {
    if (!candidateId) return;
    const requestUserId = userId;
    const requestId = ++mutationIdRef.current;
    const isCurrentRequest = () => (
      currentUserIdRef.current === requestUserId && mutationIdRef.current === requestId
    );
    setBusy(true);
    setMutationError(null);
    try {
      const updated = await setAdminTtarLink(
        requestUserId,
        confirm ? { ttar_user_id: candidateId, confirm: true } : { ttar_user_id: candidateId },
      );
      if (!isCurrentRequest()) return;
      setConfirmedLink(updated);
      setDraftId('');
      setPending(null);
      push('success', confirm ? 'TTAR ID replaced.' : 'TTAR ID attached.');
    } catch (err) {
      if (!isCurrentRequest()) return;
      setPending(null);
      setMutationError(ttarErrorMessage(err));
    } finally {
      if (isCurrentRequest()) setBusy(false);
    }
  };

  const removeLink = async () => {
    const requestUserId = userId;
    const requestId = ++mutationIdRef.current;
    const isCurrentRequest = () => (
      currentUserIdRef.current === requestUserId && mutationIdRef.current === requestId
    );
    setBusy(true);
    setMutationError(null);
    try {
      const updated = await removeAdminTtarLink(requestUserId);
      if (!isCurrentRequest()) return;
      setConfirmedLink(updated);
      setDraftId('');
      setPending(null);
      push('success', 'TTAR ID removed.');
    } catch (err) {
      if (!isCurrentRequest()) return;
      setPending(null);
      setMutationError(ttarErrorMessage(err));
    } finally {
      if (isCurrentRequest()) setBusy(false);
    }
  };

  const submitAttach = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void applyLink(false);
  };

  const submitReplace = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (candidateId) setPending('replace');
  };

  return (
    <section style={card} aria-labelledby="ttar-link-heading">
      <h2 id="ttar-link-heading" style={{ ...T.subheading, margin: 0 }}>TTAR link</h2>
      {query.loading && !link && <div style={muted}>Loading TTAR link…</div>}
      {query.error && !link && <div role="alert" style={errorBanner}>{query.error.message}</div>}
      {mutationError && <div role="alert" style={errorBanner}>{mutationError}</div>}

      {link && !link.linked && (
        <>
          <div style={muted}>Unlinked</div>
          <form onSubmit={submitAttach} style={formStyle}>
            <label style={labelStyle}>
              <span style={T.label}>TTAR user ID</span>
              <input
                aria-label="TTAR user ID"
                value={draftId}
                onChange={(event) => setDraftId(event.target.value)}
                required
                disabled={busy}
                style={inputStyle}
              />
            </label>
            <Button type="submit" disabled={busy || !candidateId}>Attach TTAR ID</Button>
          </form>
        </>
      )}

      {link?.linked && (
        <>
          <div style={details}>
            <Detail label="TTAR user ID" value={link.ttar_user_id ?? '—'} />
            <Detail label="Provenance" value={sourceLabel(link.source)} />
            <Detail label="Linked" value={linkedAtLabel(link.linked_at)} />
          </div>
          <form onSubmit={submitReplace} style={formStyle}>
            <label style={labelStyle}>
              <span style={T.label}>New TTAR user ID</span>
              <input
                aria-label="New TTAR user ID"
                value={draftId}
                onChange={(event) => setDraftId(event.target.value)}
                required
                disabled={busy}
                style={inputStyle}
              />
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button type="submit" disabled={busy || !candidateId}>Replace TTAR ID</Button>
              <Button type="button" variant="danger" disabled={busy} onClick={() => setPending('remove')}>
                Remove TTAR ID
              </Button>
            </div>
          </form>
        </>
      )}

      <ConfirmDialog
        open={pending === 'replace'}
        title="Replace TTAR ID?"
        message={`Replace TTAR ID “${link?.ttar_user_id ?? ''}” with “${candidateId}”?`}
        confirmLabel="Replace ID"
        onConfirm={() => applyLink(true)}
        onCancel={() => setPending(null)}
      />
      <ConfirmDialog
        open={pending === 'remove'}
        title="Remove TTAR ID?"
        message={`Remove TTAR ID “${link?.ttar_user_id ?? ''}” from this user? This cannot be undone.`}
        confirmLabel="Remove ID"
        destructive
        onConfirm={removeLink}
        onCancel={() => setPending(null)}
      />
    </section>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <div style={{ ...T.label, width: 96, flexShrink: 0 }}>{label}</div>
      <div style={{ ...T.ui, color: PS.nearBlack, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function sourceLabel(source: TtarLinkState['source']): string {
  if (source === 'oauth') return 'Linked by OAuth';
  if (source === 'admin') return 'Linked by admin';
  return 'Unavailable';
}

function linkedAtLabel(value: string | null): string {
  if (!value) return 'Unavailable';
  return `${formatLocal(value, 'yyyy-MM-dd')} ${formatVenueTime(value)}`;
}

function ttarErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.code === 'ttar_sub_already_linked') {
    return 'This TTAR ID is linked to another user.';
  }
  return err instanceof Error ? err.message : 'TTAR link update failed.';
}

const card: CSSProperties = {
  background: PS.white,
  border: `1px solid ${PS.borderLight}`,
  borderRadius: 4,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const details: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8 };
const formStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 10 };
const labelStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 6 };
const muted: CSSProperties = { ...T.ui, color: PS.textSec };
const errorBanner: CSSProperties = {
  padding: 12,
  background: PS.errorBg,
  border: `1px solid ${PS.errorBorder}`,
  color: PS.errorText,
  borderRadius: 3,
};
const inputStyle: CSSProperties = {
  ...T.ui,
  padding: '8px 10px',
  minHeight: 40,
  borderRadius: 3,
  border: `1px solid ${PS.border}`,
};
